import { Alert, Divider, Stack, Text } from 'braid-design-system';
import React, { useState } from 'react';

import { useStatuspage } from '#src/hooks/statuspage.tsx';

import { SmartTextLink } from 'scoobie';
import { ErrorBoundary } from '#src/components/ErrorBoundary/ErrorBoundary.tsx';
import type { SummaryResponse } from '#src/types/statuspage.ts';
import { ComponentTiles } from './components/ComponentTiles.tsx';
import {
  TopLevelStatus,
  TopLevelStatusPlaceholder,
} from './components/TopLevelStatus.tsx';
import { UpcomingMaintenance } from './components/UpcomingMaintenance/UpcomingMaintenance.tsx';
import { useDismissedMaintenances } from './useDismissedMaintenances.ts';

/**
 * Renders a view of the SEEK API's status using the Atlassian Statuspage API
 */
export const SystemStatus = () => {
  const summary = useStatuspage();

  if (!summary) {
    return <TopLevelStatusPlaceholder />;
  }

  return <Inner summary={summary} />;
};

const Inner = ({ summary }: { summary: SummaryResponse }) => {
  const [expanded, setExpanded] = useState(summary.status.indicator !== 'none');
  const [dismissedMaintenances, dismissMaintenance] =
    useDismissedMaintenances();

  const upcomingMaintenances = summary.scheduled_maintenances
    .filter(
      // This sneakily includes `completed` so we can test locally using
      // `includeScheduledMaintenances="all"`. Completed maintenances aren't
      // returned by the Statuspage summary API normally.
      ({ status }) => status === 'scheduled' || status === 'completed',
    )
    .filter(({ id }) => !dismissedMaintenances.has(id));

  return (
    <ErrorBoundary
      fallback={
        <Alert tone="caution">
          <Text>
            Unable to render the SEEK API’s status. Visit the{' '}
            <SmartTextLink href="https://status.seek.com">
              SEEK API status page
            </SmartTextLink>{' '}
            instead.
          </Text>
        </Alert>
      }
    >
      <Stack space="large">
        {upcomingMaintenances.map((maintenance) => (
          <React.Fragment key={maintenance.id}>
            <UpcomingMaintenance
              page={summary.page}
              maintenance={maintenance}
              dismiss={() => dismissMaintenance(maintenance.id)}
            />
            <Divider />
          </React.Fragment>
        ))}

        <Stack space="small">
          <TopLevelStatus
            status={summary.status}
            statusPageUrl={summary.page.url}
            pageName={summary.page.name}
            expanded={expanded}
            setExpanded={setExpanded}
          />

          {expanded ? (
            <ComponentTiles
              components={summary.components}
              statusPageUrl={summary.page.url}
            />
          ) : null}
        </Stack>
      </Stack>
    </ErrorBoundary>
  );
};
